import React, { useContext, useState } from 'react';
import PageLayout from '../pagelayout';
import { useLoader } from '../api/useLoader';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import {
  BrukerrollerDto,
  HttpError,
  MuligeStudiestedbrukereDto,
  MuligeStudiestedDto,
} from '../api/generated/studiested-generated-api';
import PanelHeader from '../components/panelheader/panelHeader';
import { ToolbarLink } from '../pagelayout/toolbarLink';
import Autocomplete from '../../src/components/input/autocomplete';
import translations from './inviteUha.i18n';
import LocalAlert, {
  AlertType,
  LocalAlertProps,
  PropsHidden,
} from '../components/notifications/localAlert';
import Button, { ButtonVariant } from '../components/button';
import {
  getMuligStudiestedDybde,
  IndentMuligStudiested,
} from '../components/indentation/indentation';
import Dropdown from '../components/input/dropdown';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import * as css from './invite.module.scss';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import { useNavigate } from 'react-router-dom';

const InviteUhaPage = () => {
  const { studiestedApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const { data, loading } = useLoader(
    () =>
      studiestedApi.inviterStudiestedbrukerGet().catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: labels.fsError,
        });
        return Promise.reject(err.body);
      }),
    [],
    false,
  );

  const [selectedUser, setSelectedUser] =
    useState<MuligeStudiestedbrukereDto>();
  const [selectedStudiested, setSelectedStudiested] =
    useState<MuligeStudiestedDto>();
  const [brukerrolle, setBrukerrolle] = useState<BrukerrollerDto>(
    BrukerrollerDto.Skriverettighet,
  );

  type ILocalAlert = LocalAlertProps | PropsHidden;
  const [alert, setAlert] = useState<ILocalAlert>({ open: false });

  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const labels = {
    title: formatMessage({ id: translations.title.id }),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    userTitle: formatMessage({ id: translations.userTitle.id }),
    userSubtitle: formatMessage({ id: translations.userSubtitle.id }),
    roleTitle: formatMessage({ id: translations.roleTitle.id }),
    roleSubtitle: formatMessage({ id: translations.roleSubtitle.id }),
    rolePermission: formatMessage({ id: translations.rolePermission.id }),
    save: formatMessage({ id: translations.save.id }),
    cancel: formatMessage({ id: translations.cancel.id }),
    success: formatMessage({ id: translations.success.id }),
    fsError: formatMessage({ id: translations.fsError.id }),
    fagpersonLabel: formatMessage({ id: translations.fagpersonLabel.id }),
    fagpersonPlaceholder: formatMessage({
      id: translations.fagpersonPlaceholder.id,
    }),
    tilgangLabel: formatMessage({ id: translations.tilgangLabel.id }),
    tilgangPlaceholder: formatMessage({
      id: translations.tilgangPlaceholder.id,
    }),
    roleChoose: formatMessage({ id: translations.roleChoose.id }),
  };

  const submitInvite = () => {
    studiestedApi
      .inviterStudiestedbrukerPost({
        nyStudiestedbrukerInvitasjonDto: {
          feideId: selectedUser && selectedUser.feideId,
          fsBrukernavn: selectedUser && selectedUser.brukernavn,
          studiestedId: selectedStudiested && selectedStudiested.id,
          brukerrolle: BrukerrollerDto[brukerrolle],
        },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.success,
        });
        navigate('/myinstitution');
      })
      .catch((err: HttpError) => {
        setAlert({
          open: true,
          contents: err.body,
          type: AlertType.ERROR,
          handleClose: () => setAlert({ open: false }),
        });
      });
  };

  const cancelButton: React.ReactNode = ToolbarLink({
    title: labels.cancel,
    exact: true,
    path: '/myinstitution',
    icon: null,
    buttonVariant: ButtonVariant.Tertiary,
  });

  const blockSubmission = (): boolean => {
    return !(selectedUser && selectedStudiested && brukerrolle);
  };

  const actionItems = (
    <>
      {cancelButton}
      <Button.Primary onClick={submitInvite} disabled={blockSubmission()}>
        {labels.save}
      </Button.Primary>
    </>
  );

  const translateRolle = (rolle: BrukerrollerDto) => {
    return formatMessage({ id: translations[`rolle${rolle}`].id });
  };

  return (
    <>
      {
        <PageLayout
          isIndented={true}
          title={labels.title}
          subtitle={labels.subtitle}
          actionItems={actionItems}
        >
          <LocalAlert {...alert} />
          <Grid container={true} spacing={3}>
            <Grid item={true} xs={12}>
              <PanelHeader
                title={labels.userTitle}
                subtitle={labels.userSubtitle}
              />
            </Grid>

            <Grid item={true} xs={4}>
              <Autocomplete
                id={'user-select'}
                onChange={(event: object, value: MuligeStudiestedbrukereDto) =>
                  setSelectedUser(value)
                }
                options={
                  data &&
                  data.muligeBrukere &&
                  data.muligeBrukere.map((bruker) => ({
                    ...bruker,
                    id: bruker.feideId,
                    navn: `${bruker.navn} (${bruker.brukernavn})`,
                  }))
                }
                label={labels.fagpersonLabel}
                placeholder={labels.fagpersonPlaceholder}
                required={true}
                loading={loading}
              />
            </Grid>

            <Grid item={true} xs={12}>
              <PanelHeader
                title={labels.roleTitle}
                subtitle={labels.roleSubtitle}
              />
            </Grid>

            <Grid item={true} xs={4}>
              <div className={css.studiestedWrapper}>
                <Autocomplete
                  id={'autocomplete'}
                  onChange={(event: object, value: MuligeStudiestedDto) =>
                    setSelectedStudiested(value)
                  }
                  options={
                    data &&
                    data.muligeSteder &&
                    data.muligeSteder.map((studiested) => ({
                      ...studiested,
                      dybde: getMuligStudiestedDybde(studiested),
                      onHoverType: StedType.STUDIESTED,
                    }))
                  }
                  renderOption={(option) => (
                    <IndentMuligStudiested studiested={option} />
                  )}
                  label={labels.tilgangLabel}
                  placeholder={labels.tilgangPlaceholder}
                  loading={loading}
                  required
                />
                {selectedStudiested && (
                  <div className={css.valueHoverCard}>
                    <OnStedHoverCard
                      sted={StedType.STUDIESTED}
                      id={selectedStudiested.id}
                      navn={selectedStudiested.navn}
                    />
                  </div>
                )}
              </div>
            </Grid>

            <Grid item={true} xs={4}>
              <Dropdown
                id={'role-select'}
                onChange={(event, value) =>
                  setBrukerrolle(value.id as BrukerrollerDto)
                }
                options={Object.keys(BrukerrollerDto).map((brukerrolle) => ({
                  id: brukerrolle,
                  navn: translateRolle(brukerrolle as BrukerrollerDto),
                }))}
                label={labels.rolePermission}
                defaultValue={{
                  id: brukerrolle,
                  navn: translateRolle(brukerrolle as BrukerrollerDto),
                }}
                placeholder={labels.roleChoose}
                required
              />
            </Grid>
          </Grid>
        </PageLayout>
      }
    </>
  );
};

export default InviteUhaPage;
